import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Container } from '../../containers/DefaultStyles/Details';
import {
    Body,
    Content,
    Footer,
    PeopleSigned,
    Details,
    DetailsContainer,
    Title
} from '../../containers/DefaultStyles/Shared';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';

import LinkButton from '../../components/Link/LinkButton';
import ShareDrawer from '../../components/ShareDrawer';
import { BackArrow } from '../../components/Shared/icons';

import { onCompleteTweet } from './actions';
import {
    setOverlayPartnerVisible,
    onGoToNextCard,
    onBackWithinCard
} from '../Widget/actions';

import { isLongText } from '../../components/Shared/commonFunctions';
import { getActionCompletedTime } from '../../utils/actionTiming';
import { ActionTypes } from '../../utils/enums';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import getTwitterShareUrl from '../../utils/tweet/tweetShare';
import {
    relevantRepresentatives,
    joinEnglish
} from '../../utils/localRepresentative/parser';
import { copyFor, capitalization } from '../../copy/copy';

const LinkText = styled.div`
    font-size: 16px;
    line-height: 24px;
    color: #333;
    font-weight: normal;
    margin-top: 12px;
    font-family: Lato;

    span {
        font-weight: 800;
        color: #ff245e;
        cursor: pointer;
    }
`;

const ButtonRow = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    width: calc(100% - 32px);
    margin: 0 auto;
`;

const Back = styled.span`
    cursor: pointer;
    margin-top: 10px;
    font-size: 10px;
    font-weight: 500;
    color: #008eff;
`;

const TweetDetails = props => (
    <Container>
        <Overlay />
        <OverlayPublisher />
        <OverlayPartner transitionType="right-to-left" />
        <Body>
            <WidgetHeader />
            <Content>
                <Title
                    isLocalRepLoading={false}
                    isLongText={isLongText(props.feedbackTitle, 40)}
                    isExtraLongText={isLongText(props.feedbackTitle, 95)}
                    onClick={e => props.setOverlayPartnerVisible(true, e, false)}
                    dangerouslySetInnerHTML={{
                        __html: props.feedbackTitle
                    }}
                />
                <DetailsContainer>
                    <Details>
                        <LinkText
                            dangerouslySetInnerHTML={{ __html: props.feedbackText }}
                            onClick={e => props.setOverlayPartnerVisible(true, e, false)}
                        />
                        <PeopleSigned>
                            {props.peopleCount}{' '}
                            {copyFor(
                                'miscellaneous',
                                'numAnswered',
                                'tweet',
                                capitalization.all
                            )}
                        </PeopleSigned>
                        <Back
                            onClick={() =>
                                props.onBackWithinCard(
                                    props.actionIdentifier,
                                    props.card,
                                    true,
                                    false
                                )
                            }
                        >
                            <BackArrow width={8} height={8} />{' '}
                            {copyFor('buttons', 'back', 'tweet', capitalization.all)}
                        </Back>
                    </Details>
                </DetailsContainer>
            </Content>
        </Body>
        <ButtonRow>
            <LinkButton
                label={props.buttonText}
                onClick={() => {
                    props.onCompleteTweet(
                        props.apiRequestData,
                        props.actionCompletedTime(),
                        props.actionIdentifier,
                        props.feedbackText,
                        props.feedbackTitle,
                        props.useLocalRep,
                        props.localRepresentativeState
                    );
                    window.open(
                        getTwitterShareUrl(
                            props.activeAction.textOriginal,
                            props.localRepTwitterMention,
                            props.localRepPostalCode,
                            props.partnerName,
                            props.publisherName,
                            props.articleUrl,
                            null
                        ),
                        'popup',
                        'height=350,width=600'
                    );

                    if (props.nextAction) {
                        props.onGoToNextCard(
                            props.nextAction.identifier,
                            props.nextCard.type,
                            props.nextAction.type,
                            props.nextAction.skipFrontCard,
                            false
                        );
                    }
                }}
            />
        </ButtonRow>
        <Footer>
            <ShareDrawer backgroundColor={'#fff'} />
        </Footer>
    </Container>
);

TweetDetails.propTypes = {
    card: PropTypes.object,
    activeAction: PropTypes.object.isRequired,
    peopleCount: PropTypes.string.isRequired,
    feedbackText: PropTypes.string.isRequired,
    feedbackTitle: PropTypes.string,
    actionCompletedTime: PropTypes.func.isRequired,
    apiRequestData: PropTypes.object.isRequired,
    onCompleteTweet: PropTypes.func.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    useLocalRep: PropTypes.bool,
    actionIdentifier: PropTypes.string.isRequired,
    localRepresentativeState: PropTypes.number,
    setOverlayPartnerVisible: PropTypes.func.isRequired,
    onBackWithinCard: PropTypes.func.isRequired,
    localRepPostalCode: PropTypes.string,
    localRepTwitterMention: PropTypes.string,
    partnerName: PropTypes.string,
    publisherName: PropTypes.string,
    nextCard: PropTypes.object,
    nextAction: PropTypes.object,
    buttonText: PropTypes.string,
    articleUrl: PropTypes.string
};

export default connect(
    state => {
        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );

        const reps = state.localRep.localRepData
            ? relevantRepresentatives(
                  state.widget.activeAction.representativeSearch,
                  state.localRep.localRepData.representative
              ).slice(0, 3)
            : [];

        return {
            activeAction: state.widget.activeAction,
            card: state.widget.cards[state.widget.activeAction.order - 1],
            nextAction,
            nextCard: nextAction ? state.widget.cards[nextAction.order - 1] : null,
            peopleCount: state.widget.activeAction.noOfAnswers || 121,
            feedbackText:
                state.widget.activeAction.thankYouText ||
                copyFor('defaultText', 'subheading', 'tweet'),
            feedbackTitle:
                state.widget.activeAction.thankYouTitle ||
                copyFor('defaultText', 'heading', 'tweet'),
            buttonText:
                state.widget.activeAction.customButtonText ||
                copyFor('buttons', 'start', 'tweet'),
            actionCompletedTime: getActionCompletedTime(state, 'tweet'),
            apiRequestData: state.widget.apiRequestData,
            useLocalRep: !!state.widget.activeAction.representativeSearch,
            actionIdentifier: state.widget.activeAction.identifier,
            localRepresentativeState: state.widget.activeAction.localRepresentativeState,
            localRepPostalCode: state.localRep.localRepData.postalCode,
            localRepTwitterMention: joinEnglish(
                reps.map(rep => {
                    if (rep.twitterMention && rep.twitterMention.length > 0) {
                        return '@' + rep.twitterMention;
                    }
                    return rep.fullName;
                })
            ),
            partnerName:
                state.widget.activeAction.partner !== undefined
                    ? state.widget.activeAction.partner.twitterMention !== undefined
                        ? '@' + state.widget.activeAction.partner.twitterMention
                        : state.widget.activeAction.partner.name
                    : 'Speakable',
            publisherName:
                state.widget.twitterMention !== undefined
                    ? '@' + state.widget.twitterMention
                    : state.widget.publisherName,
            articleUrl: state.widget.articleUrl
        };
    },
    {
        onCompleteTweet,
        setOverlayPartnerVisible,
        onGoToNextCard,
        onBackWithinCard
    },
    null,
    {
        areStatesEqual: (next, prev) => {
            if (next.widget.activeAction.type !== ActionTypes.Tweet) return true;

            return next === prev;
        }
    }
)(TweetDetails);
